import {
  Box,
  LinearProgress,
  Tooltip,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { fetchAptOverview } from 'api/apartmentProcurement'

import { useAptCategories } from './aptCategories'
import { money } from './constants'

// Sounak's tracking view — APARTMENT-WISE (2026-07-17 ask: one row per
// apartment, no category explosion; the user controls granularity). The
// Category header is a dropdown defaulting to "All categories" (numbers
// aggregate); picking one narrows every column to that category. Apartment
// and Cluster headers are typable filters (revenue-goals idiom).
const useStyles = makeStyles((theme) => ({
  section: { marginBottom: theme.spacing(2) },
  heading: { fontWeight: 700, margin: theme.spacing(1, 0) },
  over: { color: '#f44336', fontWeight: 600 },
  muted: { color: '#888' },
  kitBar: { width: 220, display: 'inline-block', verticalAlign: 'middle' },
  filterHeadCell: { padding: '6px 8px 6px 8px' },
  filterInput: {
    width: '100%',
    backgroundColor: '#fff',
    '& input': { fontSize: 13, padding: '7px 8px' },
    '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 }
  },
  categorySelect: { fontSize: 13, minWidth: 130 }
}))

const OverviewTab = () => {
  const classes = useStyles()
  const categories = useAptCategories()
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState('')
  const [aptFilter, setAptFilter] = React.useState('')
  const [clusterFilter, setClusterFilter] = React.useState('')
  const [category, setCategory] = React.useState('') // '' = all categories

  React.useEffect(() => {
    let alive = true
    fetchAptOverview()
      .then((rows) => alive && setData(rows))
      .catch(() => alive && setError('Could not load the overview.'))
    return () => {
      alive = false
    }
  }, [])

  if (error) return <Typography color="error">{error}</Typography>
  if (!data) return <LinearProgress />
  if (!data.length) {
    return (
      <Typography className={classes.muted}>
        No apartments yet — add them under At Apartment → Apartments.
      </Typography>
    )
  }

  const matches = (value, filter) =>
    !filter.trim() ||
    String(value || '').toLowerCase().includes(filter.trim().toLowerCase())

  const visibleApts = data.filter(
    (a) => matches(a.name, aptFilter) && matches(a.cluster, clusterFilter)
  )

  // One row per apartment: sum across categories, or just the selected one.
  const rows = visibleApts.map((apt) => {
    const cats = apt.categories.filter(
      (c) => !category || c.category === category
    )
    return {
      apt,
      budget: cats.reduce((s, c) => s + Number(c.budget), 0),
      used:
        cats.reduce((s, c) => s + Number(c.used), 0) +
        (category ? 0 : Number(apt.uncategorised_spend || 0)),
      remaining: cats.reduce((s, c) => s + Number(c.remaining), 0)
    }
  })
  const totals = rows.reduce(
    (t, r) => ({
      budget: t.budget + r.budget,
      used: t.used + r.used,
      remaining: t.remaining + r.remaining
    }),
    { budget: 0, used: 0, remaining: 0 }
  )

  const kitRows = visibleApts.flatMap((apt) =>
    apt.kits.map((kit) => ({ apt, ...kit }))
  )

  const headerFilter = (value, setValue, label) => (
    <TextField
      variant="outlined"
      size="small"
      className={classes.filterInput}
      placeholder={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  )

  return (
    <Box>
      <Paper variant="outlined" className={classes.section}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.filterHeadCell}>
                {headerFilter(aptFilter, setAptFilter, 'Apartment')}
              </TableCell>
              <TableCell className={classes.filterHeadCell}>
                {headerFilter(clusterFilter, setClusterFilter, 'Cluster')}
              </TableCell>
              <TableCell className={classes.filterHeadCell}>
                <Select
                  className={classes.categorySelect}
                  value={category}
                  displayEmpty
                  onChange={(e) => setCategory(e.target.value)}>
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="What Sounak allotted this apartment (one pot, no monthly reset) — summed across categories unless one is picked." arrow>
                  <span>Budget</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Spend that cleared the full approval chain (final-approved, handed over or closed)." arrow>
                  <span>Used</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Budget minus Used. Red = overspent (allowed, but flagged)." arrow>
                  <span>Remaining</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Value of requests raised but NOT yet fully approved — submitted or with an approver. Not counted in Used until Sounak approves." arrow>
                  <span>Awaiting approval</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ apt, budget, used, remaining }) => (
              <TableRow key={apt.apartment}>
                <TableCell>{apt.name}</TableCell>
                <TableCell>{apt.cluster || '—'}</TableCell>
                <TableCell>{category || 'All'}</TableCell>
                <TableCell align="right">{money(budget)}</TableCell>
                <TableCell align="right">{money(used)}</TableCell>
                <TableCell
                  align="right"
                  className={remaining < 0 ? classes.over : undefined}>
                  {money(remaining)}
                </TableCell>
                <TableCell align="right" className={classes.muted}>
                  {Number(apt.pipeline_value) ? money(apt.pipeline_value) : '—'}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" className={classes.muted}>
                    No apartments match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.length > 1 && (
              <TableRow>
                <TableCell colSpan={3} style={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {money(totals.budget)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {money(totals.used)}
                </TableCell>
                <TableCell
                  align="right"
                  style={{ fontWeight: 700 }}
                  className={totals.remaining < 0 ? classes.over : undefined}>
                  {money(totals.remaining)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography className={classes.heading}>Kit completeness</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Apartment</TableCell>
              <TableCell>Kit</TableCell>
              <TableCell>Delivered</TableCell>
              <TableCell align="right">Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kitRows.map((kit) => {
              const denominator = Math.max(kit.lines - kit.unavailable, 1)
              const pct = Math.round((kit.delivered / denominator) * 100)
              return (
                <TableRow key={kit.request}>
                  <TableCell>{kit.apt.name}</TableCell>
                  <TableCell>{kit.kit}</TableCell>
                  <TableCell>
                    {kit.delivered}/{kit.lines - kit.unavailable}
                    {kit.unavailable
                      ? ` (${kit.unavailable} unavailable)`
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    <LinearProgress
                      className={classes.kitBar}
                      variant="determinate"
                      value={Math.min(pct, 100)}
                    />{' '}
                    {pct}%
                  </TableCell>
                </TableRow>
              )
            })}
            {!kitRows.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" className={classes.muted}>
                    No kits generated for this selection yet — use Requests →
                    Generate launch kit.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

export default OverviewTab
